export const ModelEditorProtocol = 'PLAYABLE:model-editor'

export type ModelEditorProtocolType = typeof ModelEditorProtocol

export enum ModelEditorProtocolDataType {
  previewReady = 'previewReady',
  transformType = 'transform',
  hsvType = 'hsv',
  cameraAdapt = 'cameraAdapt',
  cameraOrbit = 'cameraOrbit',
  shaderParam = 'shaderParam',
  playAnimation = 'playAnimation', // 切换动作
  setMaterial = 'setMaterial', // 材质替换
  animationSpeedChangedEvent = 'animationSpeedChangedEvent', // 动画速度改变
  animationEventChangedEvent = 'animationEventChangedEvent', // 动画事件改变
  linkageUpdate = 'linkageUpdate', // 联动更新
  moduleEnableEvent = 'ModuleEnableEvent',
}

type PreviewModule =
  | 'reference'
  | 'statistic'
  | 'grid'
  | 'operation'
  | 'animation'

export enum LinkageUpdateType {
  animationEventChangedEvent,
}

export type ModelResId = string | string[]

export type HsvValue = [number, number, number] | null

export interface ShaderParams {
  mainColor: [number, number, number, number]
  occlusion: number
  roughness: number
  metallic: number
  specularIntensity: number
  emissive: [number, number, number, number]
}

// 新增动画预览数据，详细请看下面的ModelModifyData
export interface AnimationFrameEvent {
  /** 事件对应的时间（注意：不是帧） */
  time: number
  /** 事件类型，（注意：有可能是null，表示不是伤害也不是特效） */
  type: 'Damage' | 'Effect' | null
}

export interface MaterialData {
  /** 唯一id，其实就是材质id */
  id: string
  /** hsv可编辑部位，目前最多四个部位，null表示不可编辑 */
  hsv?: Array<[number, number, number] | null>
  /** 材质的shader参数，此参数内部全部为option，即有可能有，有可能没有。 */
  shaderParams?: Partial<ShaderParams>
}

// 新增动画预览数据，详细请看下面的ModelModifyData
export interface AnimationData {
  /** 唯一id，其实就是动画资源id */
  id: string
  /** 动画播放速率 */
  speed: number
  /** 动画时长 */
  duration: number
  /** 动画帧率 */
  frameRate: number
  /** 动画旋转角度 */
  rotate?: number
  /** 事件数组，time就是动画事件对应在动画里面的时间（注意，不是帧） */
  events: (AnimationFrameEvent | null)[] | (null | number)[]
}

export interface ModelModifyData {
  /**
   * @param id 唯一id，模型修改时，可能是Mesh，也可能是Skeleton，所以有多个
   */
  transform?: {
    id: ModelResId
  }
  /**
   * @param id 唯一id，其实就是材质id
   * @param editorPartMask hsv可编辑部位掩码，0表示无编辑部位，可编辑部位 |= 1 << IndexPART
   */
  material?: MaterialData
  animation?: AnimationData
  partialRefresh: boolean
}

export type PropertyModify = 'add' | 'remove' | 'update'

export interface CameraOrbit {
  polarAngle: number
  azimuthAngle: number
  radialDistance: number
  viewOffsetX: number
  viewOffsetY: number
  viewOffsetZ: number
  dampingFactor: number
}

export enum AdaptRatioType {
  IPhone6_Landscape = 17787,
  IPhone6_Portrait = 5622,
  IPhoneX_Landscape = 21653,
  IPhoneX_Portrait = 4618,
  IPad_Landscape = 13333,
  IPad_Portrait = 7500,
}

export type TransformTypes = 'position' | 'eulerAngles' | 'scale'
export type VectorKeys = 'x' | 'y' | 'z'
/** 对应通道：R、G、B、A */
type IndexPART = 0 | 1 | 2 | 3

export enum HSVValueType {
  /** 色相 */
  Hue,
  /** 饱和度加减 */
  Saturation,
  /** 亮度加减 */
  Brightness,
}

export enum TransformType {
  translate = 0,
  rotate = 1,
  scale = 2,
}

export type ModelEditorProtocolData =
  | {
      type: ModelEditorProtocolDataType.previewReady
      data: ModelModifyData
    }
  | {
      type: ModelEditorProtocolDataType.transformType
      data: {
        transformType: TransformTypes
        vectorKey: VectorKeys
        value: number
      }
    }
  | {
      type: ModelEditorProtocolDataType.hsvType
      data: {
        indexPart: IndexPART
        indexHsv: HSVValueType
        value: number
      }
    }
  | {
      type: ModelEditorProtocolDataType.cameraAdapt
      data: {
        type: PropertyModify // 基本都是传 update
        ratio: AdaptRatioType // IPhone6_Landscape = 17787, IPhone6_Portrait = 5622,
        propertyName?: 'fovOffset' | 'orthoHeightOffset'
        value: number
      }
    }
  | {
      type: ModelEditorProtocolDataType.cameraOrbit
      data: {
        name: string
        propertyName: keyof CameraOrbit
        value: number
      }
    }
  | {
      type: ModelEditorProtocolDataType.shaderParam
      data: {
        key: keyof ShaderParams
        value: ShaderParams[keyof ShaderParams]
      }
    }
  | {
      type: ModelEditorProtocolDataType.playAnimation
      data: {
        path: string
      }
    }
  | {
      type: ModelEditorProtocolDataType.setMaterial
      data: {
        path: string
      }
    }
  | {
      type: ModelEditorProtocolDataType.animationSpeedChangedEvent
      data: {
        speed: number
      }
    }
  | {
      type: ModelEditorProtocolDataType.linkageUpdate
      data: {
        linkageType: LinkageUpdateType.animationEventChangedEvent
        index: number
        time: number
        type: string
      }
    }
  | {
      type: ModelEditorProtocolDataType.animationEventChangedEvent
      data: {
        index: number
        time: number
      }
    }
  | {
      type: ModelEditorProtocolDataType.moduleEnableEvent
      data: {
        key: PreviewModule
        value: boolean
      }
    }

export interface ModelEditorProtocolMessage {
  type: ModelEditorProtocolType
  data: ModelEditorProtocolData
}

export type ModelModify = {
  transforms?: {
    id: ModelResId
    value: number[]
  }[]
  materials?: {
    id: ModelResId
    hsvs: HsvValue[]
    shaderParams: Partial<ShaderParams>
  }[]
  animations?: AnimationData[]
}

export interface CameraAdaptData {
  ratio: AdaptRatioType
  fovOffset: number
  orthoHeightOffset: number
}

export interface OrbitParams {
  name: string
  displayName: string
  polarAngle: number
  azimuthAngle: number
  radialDistance: number
  viewOffsetX: number
  viewOffsetY: number
  viewOffsetZ: number
  dampingFactor: number
}

export interface CameraAdaptConfig {
  adapt: {
    adaptDatas: CameraAdaptData[]
  }
  orbitParams: OrbitParams[]
}

export interface ReplaceData {
  type: 'gameConfig' | 'assetsPackage'
  key: string
  value: string
  prefix?: string // 只有模型贴图才有的字段，通过 prefix 去找对应的 resource 做更新
  file?: string // 平台中，更新贴图的时候用的是 portal:// 协议的路径
}
